import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { annualBreakdown, annualMarkdown } from '../utils/annualBreakdown';

const pf = (v) => (v === null ? 'n/a' : v.toFixed(2));
const sign = (v) => (v >= 0 ? 'success.main' : 'error.main');

/**
 * ONE ROW PER CALENDAR YEAR of the run that is currently on screen.
 *
 * <h2>It is derived from the TRADES, so it tracks the parameters automatically</h2>
 *
 * The rows are computed from `trades` with {@code useMemo`, so every re-run — a different Time
 * Ejector, a wider ATR stop, another provider — produces a new array and a new table with no
 * wiring of its own. Nothing here caches across runs and nothing has to be invalidated.
 *
 * <h2>Why a year's figures are not slices of the global ones</h2>
 *
 * Net PnL is COMPOUNDED and drawdown is PATH-DEPENDENT, so neither can be apportioned: the
 * yearly returns do not sum to the total and the yearly drawdowns do not max to it. Each year is
 * computed from its own trades, starting from a fresh curve, which is what makes "2009" a
 * statement about 2009 rather than about everything up to it.
 *
 * <p>The ALL row is therefore not a column sum — it is the same arithmetic over every trade, and
 * it exists to be checked against the backend's own figures in the panel above. A test asserts
 * they agree; this is the row a reader can check by eye.
 */
const DynaManAnnualTable = ({ trades, symbol = '', provider = '', engineRows = [] }) => {
  const [copied, setCopied] = useState(false);
  /**
   * THE SHARED COLUMNS COME FROM ONE CALCULATOR, the extra two from the backend.
   *
   * <p>`annualBreakdown` still owns trades, W/L, win rate, profit factor, net PnL and drawdown —
   * it mirrors `LedgerStatistics` loop for loop and is pinned against it, so rendering the
   * backend's own numbers for those would put two answers to one question a centimetre apart.
   *
   * <p>What the backend knows and this cannot is BYPASSED NODES, which come from the rejection
   * log, and POINTS, which need the account's fills. So those three are merged in BY YEAR rather
   * than recomputed. A year the backend did not report leaves them blank rather than zero: a
   * zero bypass count is a year in which every armed node confirmed, and that is a finding, not
   * the absence of data.
   */
  const byYear = useMemo(
    // KEYED ON A STRING, because the two sides disagree about the TYPE and agreed about the
    // name. `annualBreakdown` labels a row with the four characters it sliced out of the exit
    // timestamp; the backend ships a Java `int`. A Map lookup of 1993 against '1993' misses
    // EVERY year -- and the All row, which iterates the values rather than looking one up, kept
    // working, so the table rendered a full column of dashes above a correct total. Found in a
    // browser, not by a test: both sides were right about the name and the source-grep that
    // checked the wiring could not see the type.
    () => new Map((engineRows || []).map((r) => [String(r.year), r])), [engineRows],
  );
  const hasEngineRows = byYear.size > 0;
  const { rows, total } = useMemo(() => annualBreakdown(trades), [trades]);

  if (!rows.length) return null;

  const caption = `**DynaMan — ${symbol || 'symbol'} · ${provider || 'provider'}** — annual breakdown`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(annualMarkdown({ rows, total }, caption));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // A clipboard write can be refused — no permission, an insecure origin, a browser that
      // only allows it from a trusted gesture. Saying so beats a button that looks like it
      // worked, because the reader's next action is to paste something that is not there.
      setCopied('failed');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const cells = (r, bold) => (
    <>
      <TableCell sx={{ fontWeight: bold ? 700 : 400 }}>{r.year}</TableCell>
      <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400 }}>{r.trades}</TableCell>
      <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400 }}>
        <Box component="span" sx={{ color: 'success.main' }}>{r.wins}</Box>
        {' / '}
        <Box component="span" sx={{ color: 'error.main' }}>{r.losses}</Box>
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400 }}>
        {r.winRatePercent.toFixed(1)}%
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400 }}>{pf(r.profitFactor)}</TableCell>
      <TableCell align="right"
        sx={{ fontWeight: bold ? 700 : 400, color: sign(r.netPnlPercent) }}>
        {r.netPnlPercent >= 0 ? '+' : ''}{r.netPnlPercent.toFixed(2)}%
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400 }}>
        {r.maxDrawdownPercent.toFixed(2)}%
      </TableCell>
      {hasEngineRows && (
        <>
          <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400 }}>
            {bypassedFor(r, byYear)}
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400 }}>
            {pointsFor(r, byYear, 'grossPoints')}
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400 }}>
            {pointsFor(r, byYear, 'netPoints')}
          </TableCell>
        </>
      )}
    </>
  );

  return (
    <Box sx={{ mt: 2 }} data-testid="annual-breakdown">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
          Annual breakdown
        </Typography>
        <Tooltip title="Copy as a Markdown table, with a blank row for your manual book">
          <Button size="small" startIcon={<ContentCopyIcon fontSize="small" />} onClick={copy}
            data-testid="copy-annual-markdown">
            {copied === 'failed' ? 'Copy failed' : (copied ? 'Copied' : 'Copy as Markdown')}
          </Button>
        </Tooltip>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Grouped by EXIT year, realised trades only. Net PnL is each year&apos;s own compounded
        return and drawdown is measured from that year&apos;s own peak — so the years do not sum
        to the total, and the All row is the same arithmetic over every trade rather than a
        column sum.
        {hasEngineRows && ' BYPASSED counts armed timing nodes the market never confirmed — '
          + 'they produced no trade at all, so they appear in no other column here. Points are '
          + 'SUMMED and weight the recent years, which a 15x price range makes large.'}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Year</TableCell>
            <TableCell align="right">Trades</TableCell>
            <TableCell align="right">W / L</TableCell>
            <TableCell align="right">Win rate</TableCell>
            <TableCell align="right">Profit factor</TableCell>
            <TableCell align="right">Net PnL</TableCell>
            <TableCell align="right">Max DD</TableCell>
            {hasEngineRows && (
              <>
                <TableCell align="right">Bypassed</TableCell>
                <TableCell align="right">Gross pts</TableCell>
                <TableCell align="right">Net pts</TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => <TableRow key={r.year} hover>{cells(r, false)}</TableRow>)}
          <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider' } }}>
            {cells(total, true)}
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

/** The backend's bypassed count for a year, or a dash where it reported none. */
const bypassedFor = (row, byYear) => {
  const found = byYear.get(String(row.year));
  if (found) return found.bypassed;
  // THE ALL ROW sums the years, because a count IS summable -- unlike every other column here,
  // which is compounded or path-dependent and deliberately is not.
  if (row.year === 'All') {
    return [...byYear.values()].reduce((n, r) => n + r.bypassed, 0);
  }
  return '—';
};

/** A points column, summed for the All row, blank where the backend reported nothing. */
const pointsFor = (row, byYear, key) => {
  const sum = (rs) => {
    const vals = rs.map((r) => r[key]).filter((v) => typeof v === 'number');
    return vals.length === 0 ? null : vals.reduce((a, b) => a + b, 0);
  };
  const value = row.year === 'All'
    ? sum([...byYear.values()])
    : (byYear.has(String(row.year)) ? sum([byYear.get(String(row.year))]) : null);
  // NULL is "the run was not sized" for net points, and "no row" otherwise. A zero would read as
  // a year that broke even, which is a different and real result.
  return value === null ? '—' : `${value >= 0 ? '+' : ''}${value.toFixed(2)}`;
};

DynaManAnnualTable.propTypes = {
  /** The run's completed trades, in the order they were taken — drawdown is path-dependent. */
  /**
   * The backend's per-year rows, for the two columns this component cannot derive.
   *
   * <p>Empty on a run that predates them, which hides the three extra columns entirely rather
   * than showing a table of dashes.
   */
  // eslint-disable-next-line react/forbid-prop-types
  engineRows: PropTypes.array,
  trades: PropTypes.arrayOf(PropTypes.shape({
    exitTime: PropTypes.string,
    pnlPercent: PropTypes.number,
    open: PropTypes.bool,
  })),
  /** Named in the copied caption, so a pasted table says what it is about. */
  symbol: PropTypes.string,
  provider: PropTypes.string,
};

export default DynaManAnnualTable;
